import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { Search, LogOut, Edit3, Trash2, Check, Star, Eye, Filter, ChartBar as BarChartIcon } from "lucide-react";
import { adminLogin, isAdminLoggedIn, adminStats, adminSearchProperties, adminApproveProperty, adminUpdateProperty, adminBulkAction, adminDeleteProperty, adminToggleFeature, adminEnquiries, adminUpdateEnquiry, adminDeleteEnquiry, adminCreateBlog, adminDeleteBlog, adminLogout, fetchBlog, adminAnalytics, adminGetPage, adminSetPage } from "@/lib/api";
import { toast } from "sonner";
import { COMPANY, CATEGORIES } from "@/lib/constants";

const GOLD = ["#C9A961", "#0A1F44", "#A88940", "#E4C687", "#4B5563", "#8B7355", "#B8956A", "#6B4E20", "#F2D799", "#7A5C33", "#95773F"];
const STATUS_COLORS = { new: "bg-blue-500", contacted: "bg-yellow-500", converted: "bg-green-500", closed: "bg-gray-500", lost: "bg-red-500" };

export function AdminLogin() {
  const [email, setEmail] = useState("wilshirebuildcon@gmail.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  useEffect(() => { if (isAdminLoggedIn()) nav("/admin"); }, [nav]);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await adminLogin(email, password); toast.success("Welcome back"); nav("/admin"); }
    catch (err) { toast.error(err.response?.data?.detail || "Invalid credentials"); }
    setLoading(false);
  };
  return (
    <div className="min-h-screen bg-brand-ivory flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-brand-gold/30 p-8 shadow-lg">
        <div className="text-center mb-6">
          <img src={COMPANY.logo} alt="" className="h-16 w-16 object-contain mx-auto" />
          <div className="divider-gold mt-4">Admin Portal</div>
          <h1 className="mt-3 font-serif text-3xl text-brand-navy">Sign in</h1>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Email</label>
            <Input required data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Password</label>
            <Input required type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1" />
          </div>
          <Button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full h-11 rounded-sm bg-brand-navy text-brand-gold hover:bg-brand-navy/90">{loading ? "Signing in..." : "Sign in"}</Button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [props, setProps] = useState([]);
  const [enq, setEnq] = useState([]);
  const [blog, setBlog] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [filterApproved, setFilterApproved] = useState("all");
  const [editProp, setEditProp] = useState(null);
  const [pages, setPages] = useState({ about: "", services: "" });

  useEffect(() => {
    if (!isAdminLoggedIn()) { nav("/admin/login"); return; }
    refresh();
    loadPages();
  }, []);

  useEffect(() => { if (isAdminLoggedIn()) loadProps(); /* eslint-disable-next-line */ }, [q, filterCategory, filterSource, filterApproved]);

  const loadProps = async () => {
    try {
      const params = {};
      if (q) params.search = q;
      if (filterCategory !== "all") params.category = filterCategory;
      if (filterSource !== "all") params.source = filterSource;
      if (filterApproved === "pending") params.pending = true;
      const list = await adminSearchProperties(params);
      const filtered = filterApproved === "approved" ? list.filter(p => p.approved) : list;
      setProps(filtered);
    } catch (e) { if (e.response?.status === 401) { adminLogout(); nav("/admin/login"); } }
  };

  const refresh = async () => {
    try {
      setStats(await adminStats());
      setAnalytics(await adminAnalytics());
      loadProps();
      setEnq(await adminEnquiries());
      setBlog(await fetchBlog());
    } catch (e) { if (e.response?.status === 401) { adminLogout(); nav("/admin/login"); } }
  };

  const loadPages = async () => {
    const [ab, sv] = await Promise.all([adminGetPage("about"), adminGetPage("services")]);
    setPages({ about: ab.content || "", services: sv.content || "" });
  };

  const doApprove = async (id) => { await adminApproveProperty(id); toast.success("Approved"); refresh(); };
  const doDelete = async (id) => { if (!window.confirm("Delete?")) return; await adminDeleteProperty(id); toast.success("Deleted"); refresh(); };
  const doFeature = async (id) => { await adminToggleFeature(id); toast.success("Toggled"); refresh(); };

  const doBulk = async (action) => {
    if (selected.length === 0) return toast.error("Select at least one property");
    if (action === "delete" && !window.confirm(`Delete ${selected.length} properties?`)) return;
    await adminBulkAction(selected, action);
    toast.success(`Bulk ${action} on ${selected.length} items`);
    setSelected([]);
    refresh();
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAll = () => setSelected(props.map(p => p.id));
  const clearSelection = () => setSelected([]);

  const doEnqStatus = async (id, status) => { await adminUpdateEnquiry(id, { status }); toast.success("Updated"); refresh(); };
  const doDelEnq = async (id) => { if (!window.confirm("Delete?")) return; await adminDeleteEnquiry(id); toast.success("Deleted"); refresh(); };

  const [newBlog, setNewBlog] = useState({ title: "", slug: "", excerpt: "", content: "", cover: "", tags: "" });
  const createBlog = async (e) => {
    e.preventDefault();
    try {
      await adminCreateBlog({ ...newBlog, tags: newBlog.tags.split(",").map(t => t.trim()).filter(Boolean) });
      toast.success("Post created");
      setNewBlog({ title: "", slug: "", excerpt: "", content: "", cover: "", tags: "" });
      refresh();
    } catch { toast.error("Failed"); }
  };
  const delBlog = async (id) => { if (!window.confirm("Delete?")) return; await adminDeleteBlog(id); toast.success("Deleted"); refresh(); };

  const savePage = async (key) => { await adminSetPage(key, pages[key]); toast.success(`Saved ${key}`); };
  const logout = () => { adminLogout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-brand-ivory pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="divider-gold">Admin Console</div>
            <h1 className="mt-3 font-serif text-4xl text-brand-navy">Dashboard</h1>
            <p className="text-sm text-brand-charcoal/60 mt-1">Welcome back — full control of your portal</p>
          </div>
          <Button onClick={logout} variant="outline" data-testid="admin-logout" className="rounded-sm border-brand-navy text-brand-navy"><LogOut size={14} className="mr-2" />Logout</Button>
        </div>

        {/* Stat cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { l: "Total Properties", v: stats.properties_total, c: "text-brand-navy" },
              { l: "Pending Approval", v: stats.properties_pending, c: "text-brand-gold", hi: stats.properties_pending > 0 },
              { l: "Enquiries", v: stats.enquiries_total, c: "text-brand-navy" },
              { l: "Blog Posts", v: stats.blog_total, c: "text-brand-navy" },
            ].map((s) => (
              <div key={s.l} className={`p-6 border ${s.hi ? "border-brand-gold bg-brand-gold/10 shadow-md" : "border-brand-gold/30 bg-white"}`}>
                <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">{s.l}</div>
                <div className={`mt-2 font-serif text-4xl ${s.c}`}>{s.v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Analytics charts */}
        {analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
            <ChartCard title="Properties by Category">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={analytics.categories} dataKey="value" nameKey="name" outerRadius={80} label={(e) => `${e.name} (${e.value})`}>
                    {analytics.categories.map((_, i) => <Cell key={i} fill={GOLD[i % GOLD.length]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Purpose Split">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.purposes}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#C9A961" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Top Cities">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.cities} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#0A1F44" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        )}

        <Tabs defaultValue="props">
          <TabsList className="bg-white border border-brand-gold/20 rounded-none">
            <TabsTrigger value="props" data-testid="tab-props" className="rounded-none">Properties</TabsTrigger>
            <TabsTrigger value="enq" data-testid="tab-enq" className="rounded-none">Enquiries ({enq.length})</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog" className="rounded-none">Blog</TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages" className="rounded-none">CMS Pages</TabsTrigger>
          </TabsList>

          <TabsContent value="props" className="mt-4 space-y-4">
            {/* Filters */}
            <div className="bg-white border border-brand-gold/20 p-4 flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/50" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, location, city..." className="pl-9 rounded-sm h-10" data-testid="admin-search" />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40 rounded-sm h-10" data-testid="admin-filter-cat"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className="w-36 rounded-sm h-10"><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterApproved} onValueChange={setFilterApproved}>
                <SelectTrigger className="w-40 rounded-sm h-10"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bulk actions */}
            {selected.length > 0 && (
              <div className="bg-brand-navy text-white p-3 flex flex-wrap items-center gap-3">
                <span className="text-sm">{selected.length} selected</span>
                <Button size="sm" onClick={() => doBulk("approve")} data-testid="bulk-approve" className="rounded-sm bg-brand-gold text-brand-navy h-8">Approve</Button>
                <Button size="sm" onClick={() => doBulk("feature")} className="rounded-sm bg-white text-brand-navy h-8">Feature</Button>
                <Button size="sm" onClick={() => doBulk("unfeature")} variant="outline" className="rounded-sm h-8 text-white border-white">Unfeature</Button>
                <Button size="sm" onClick={() => doBulk("delete")} className="rounded-sm bg-red-600 h-8">Delete</Button>
                <Button size="sm" variant="ghost" onClick={clearSelection} className="text-white h-8 ml-auto">Clear</Button>
              </div>
            )}

            {/* Property table */}
            <div className="bg-white border border-brand-gold/20 divide-y divide-brand-gold/10">
              <div className="p-3 flex items-center gap-3 bg-brand-cream/50 text-[10px] uppercase tracking-widest text-brand-charcoal/70">
                <input type="checkbox" checked={selected.length === props.length && props.length > 0} onChange={() => selected.length === props.length ? clearSelection() : selectAll()} />
                <span className="flex-1">Property ({props.length})</span>
                <span className="w-32 text-right">Actions</span>
              </div>
              {props.length === 0 && <div className="p-6 text-sm text-brand-charcoal/60 text-center">No properties match filters.</div>}
              {props.map((p) => (
                <div key={p.id} className="p-4 flex flex-wrap items-center gap-3">
                  <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} data-testid={`sel-${p.id}`} />
                  <img src={p.images?.[0]} alt="" className="w-16 h-16 object-cover bg-brand-cream" />
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-serif text-lg text-brand-navy flex items-center gap-2">
                      {p.title}
                      {p.featured && <Star size={14} className="text-brand-gold fill-brand-gold" />}
                    </div>
                    <div className="text-xs text-brand-charcoal/60 flex flex-wrap gap-2">
                      <span className="uppercase">{p.category}</span> ·
                      <span>{p.city}</span> ·
                      <span className="text-brand-gold font-bold">{p.price_label}</span> ·
                      <span className="uppercase text-[10px]">{p.source}</span>
                    </div>
                    <div className="mt-1 flex gap-2">
                      {!p.approved && <span className="text-[10px] uppercase tracking-widest bg-brand-gold/20 text-brand-gold px-2 py-0.5">Pending</span>}
                      {p.approved && <span className="text-[10px] uppercase tracking-widest bg-green-100 text-green-700 px-2 py-0.5">Live</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {!p.approved && <Button size="sm" onClick={() => doApprove(p.id)} data-testid={`approve-${p.id}`} className="rounded-sm bg-brand-gold text-brand-navy h-8 px-2"><Check size={12} /></Button>}
                    <Button size="sm" variant="outline" onClick={() => setEditProp(p)} data-testid={`edit-${p.id}`} className="rounded-sm h-8 px-2"><Edit3 size={12} /></Button>
                    <Button size="sm" variant="outline" onClick={() => doFeature(p.id)} className="rounded-sm h-8 px-2" title={p.featured ? "Unfeature" : "Feature"}><Star size={12} className={p.featured ? "fill-brand-gold text-brand-gold" : ""} /></Button>
                    <a href={`/properties/${p.id}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-sm h-8 px-2"><Eye size={12} /></Button></a>
                    <Button size="sm" variant="outline" onClick={() => doDelete(p.id)} data-testid={`del-prop-${p.id}`} className="rounded-sm h-8 px-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"><Trash2 size={12} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="enq" className="mt-4">
            <div className="bg-white border border-brand-gold/20 divide-y divide-brand-gold/10">
              {enq.length === 0 && <div className="p-6 text-sm text-brand-charcoal/60">No enquiries yet.</div>}
              {enq.map((e) => (
                <div key={e.id} className="p-4 flex flex-wrap items-center gap-4 justify-between">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-3">
                      <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[e.status || 'new']}`} />
                      <div className="font-serif text-lg text-brand-navy">{e.name}</div>
                      <span className="text-xs text-brand-charcoal/60">{e.phone}</span>
                    </div>
                    <div className="text-xs text-brand-charcoal/60 ml-5">{e.email} {e.property_id && `· Property: ${e.property_id.slice(0,8)}`}</div>
                    {e.message && <div className="text-sm mt-1 text-brand-charcoal/80 ml-5">&ldquo;{e.message}&rdquo;</div>}
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <Select value={e.status || 'new'} onValueChange={(v) => doEnqStatus(e.id, v)}>
                      <SelectTrigger className="w-32 h-8 text-xs rounded-sm" data-testid={`enq-status-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                      </SelectContent>
                    </Select>
                    <a href={`tel:${e.phone}`}><Button size="sm" className="rounded-sm bg-brand-navy text-brand-gold h-8">Call</Button></a>
                    <a href={`https://wa.me/91${e.phone.replace(/\D/g,'').slice(-10)}?text=${encodeURIComponent(`Hello ${e.name}, from Wilshire Buildcon following up on your enquiry.`)}`} target="_blank" rel="noreferrer">
                      <Button size="sm" className="rounded-sm bg-[#25D366] hover:bg-[#1fb856] text-white h-8">WhatsApp</Button>
                    </a>
                    <Button size="sm" variant="outline" onClick={() => doDelEnq(e.id)} data-testid={`del-enq-${e.id}`} className="rounded-sm h-8">Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="blog" className="mt-4 space-y-6">
            <form onSubmit={createBlog} className="bg-white border border-brand-gold/20 p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input required placeholder="Title" data-testid="blog-title" value={newBlog.title} onChange={(e) => setNewBlog({ ...newBlog, title: e.target.value })} />
              <Input required placeholder="Slug (e.g. my-post)" data-testid="blog-slug" value={newBlog.slug} onChange={(e) => setNewBlog({ ...newBlog, slug: e.target.value })} />
              <Input placeholder="Cover image URL" value={newBlog.cover} onChange={(e) => setNewBlog({ ...newBlog, cover: e.target.value })} className="md:col-span-2" />
              <Input placeholder="Tags (comma separated)" value={newBlog.tags} onChange={(e) => setNewBlog({ ...newBlog, tags: e.target.value })} className="md:col-span-2" />
              <Textarea placeholder="Excerpt" value={newBlog.excerpt} onChange={(e) => setNewBlog({ ...newBlog, excerpt: e.target.value })} className="md:col-span-2" />
              <Textarea placeholder="Content" rows={5} value={newBlog.content} onChange={(e) => setNewBlog({ ...newBlog, content: e.target.value })} className="md:col-span-2" />
              <Button type="submit" data-testid="blog-create" className="rounded-sm bg-brand-navy text-brand-gold md:col-span-2">Publish Post</Button>
            </form>
            <div className="bg-white border border-brand-gold/20 divide-y divide-brand-gold/10">
              {blog.map((b) => (
                <div key={b.id} className="p-4 flex items-center gap-4 justify-between">
                  <div className="flex items-center gap-3">
                    {b.cover && <img src={b.cover} alt="" className="w-16 h-16 object-cover" />}
                    <div>
                      <div className="font-serif text-lg text-brand-navy">{b.title}</div>
                      <div className="text-xs text-brand-charcoal/60">/blog/{b.slug}</div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => delBlog(b.id)} className="rounded-sm">Delete</Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="pages" className="mt-4 space-y-6">
            <div className="bg-white border border-brand-gold/20 p-6">
              <h3 className="font-serif text-2xl text-brand-navy mb-3">About Page Content</h3>
              <p className="text-xs text-brand-charcoal/60 mb-3">Edit the extra content shown on the About page. Supports plain text or HTML.</p>
              <Textarea rows={8} value={pages.about} onChange={(e) => setPages({ ...pages, about: e.target.value })} placeholder="Enter About page content..." className="rounded-sm mb-3" data-testid="page-about" />
              <Button onClick={() => savePage("about")} className="rounded-sm bg-brand-navy text-brand-gold">Save About</Button>
            </div>
            <div className="bg-white border border-brand-gold/20 p-6">
              <h3 className="font-serif text-2xl text-brand-navy mb-3">Services Page Content</h3>
              <Textarea rows={8} value={pages.services} onChange={(e) => setPages({ ...pages, services: e.target.value })} placeholder="Enter Services page content..." className="rounded-sm mb-3" data-testid="page-services" />
              <Button onClick={() => savePage("services")} className="rounded-sm bg-brand-navy text-brand-gold">Save Services</Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit modal */}
      {editProp && <EditPropertyModal p={editProp} onClose={() => setEditProp(null)} onSaved={() => { setEditProp(null); refresh(); }} />}
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white border border-brand-gold/20 p-4">
      <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/60 mb-2 flex items-center gap-1"><BarChartIcon size={10} className="text-brand-gold" /> {title}</div>
      {children}
    </div>
  );
}

function EditPropertyModal({ p, onClose, onSaved }) {
  const [f, setF] = useState({ ...p });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateProperty(p.id, {
        title: f.title, description: f.description, category: f.category, purpose: f.purpose,
        city: f.city, state: f.state, location: f.location, price: Number(f.price) || 0,
        price_label: f.price_label, area: Number(f.area) || 0, area_unit: f.area_unit,
        bhk: f.bhk ? Number(f.bhk) : null, status: f.status, approved: f.approved, featured: f.featured,
      });
      toast.success("Property updated");
      onSaved();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-navy">Edit Property</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Title</label>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-sm mt-1" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Description</label>
            <Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-sm mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Category</label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Purpose</label>
            <Select value={f.purpose} onValueChange={(v) => setF({ ...f, purpose: v })}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sell">Sell</SelectItem><SelectItem value="rent">Rent</SelectItem>
                <SelectItem value="lease">Lease</SelectItem><SelectItem value="jv">Joint Venture</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">City</label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Location</label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Price (₹)</label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Price Label</label><Input value={f.price_label} onChange={(e) => setF({ ...f, price_label: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Area</label><Input type="number" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">BHK</label><Input type="number" value={f.bhk || ""} onChange={(e) => setF({ ...f, bhk: e.target.value })} className="rounded-sm mt-1" /></div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Status</label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
                <SelectItem value="rented">Rented</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4 md:col-span-2 pt-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.approved} onChange={(e) => setF({ ...f, approved: e.target.checked })} /> Approved</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Featured</label>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-edit" className="rounded-sm bg-brand-navy text-brand-gold">{saving ? "Saving..." : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
